import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";

const ServiceCard = ({
  service,
  index,
  showLearnMore = true,
}) => {
  const Icon = service.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.45,
        delay: index * 0.08,
      }}
      whileHover={{
        y: -8,
        transition: {
          type: "spring",
          stiffness: 220,
          damping: 18,
        },
      }}
      whileTap={{
        scale: 0.97,
      }}
      className="
        group
        relative
        h-full
        overflow-hidden
        rounded-[24px]
        md:rounded-[30px]
        bg-white
        border
        border-slate-100
        transition-all
        duration-700
        ease-out
        shadow-[0_6px_18px_rgba(15,23,42,.06),0_12px_30px_rgba(15,23,42,.08)]
        md:shadow-[0_8px_30px_rgba(15,23,42,.05),0_25px_60px_rgba(15,23,42,.06)]
        active:scale-[0.98]
        hover:-translate-y-3
        hover:scale-[1.02]
        hover:border-pink-200
        hover:shadow-[0_25px_70px_rgba(236,72,153,.18),0_15px_35px_rgba(59,130,246,.12)]
      "
    >
      {/* Hover Gradient Border */}
      <div
        className={`
          absolute
          inset-0
          rounded-[24px]
          md:rounded-[30px]
          opacity-100
          md:opacity-0
          md:group-hover:opacity-100
          transition-all
          duration-700
          bg-gradient-to-r
          ${service.gradient}
          p-[1px]
        `}
      >
        <div className="h-full w-full rounded-[24px] md:rounded-[30px] bg-white" />
      </div>

      {/* Border Ring */}
      <div className="absolute inset-0 rounded-[24px] md:rounded-[26px] ring-1 ring-transparent transition-all duration-500 md:group-hover:ring-slate-200/70" />

      {/* Animated Glow */}
      <motion.div
        className={`
          absolute
          -top-24
          -right-24
          h-52
          w-52
          md:h-60
          md:w-60
          rounded-full
          bg-gradient-to-r
          ${service.gradient}
          blur-[90px]
          md:blur-[100px]
        `}
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.05, 0.13, 0.05],
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Shine Effect */}
      <div
        className="
          absolute
          left-[-120%]
          top-0
          h-full
          w-[60%]
          rotate-12
          bg-gradient-to-r
          from-transparent
          via-white/70
          to-transparent
          transition-all
          duration-[1200ms]
          md:group-hover:left-[150%]
        "
      />

      {/* Decorative Dots */}
      <div className="absolute right-4 top-4 md:right-6 md:top-6 opacity-20">
        <div className="grid grid-cols-4 gap-1">
          {Array.from({ length: 16 }).map((_, i) => (
            <div
              key={i}
              className="h-1 w-1 rounded-full bg-slate-400"
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative flex h-full flex-col p-3 sm:p-4 md:p-5">

        {/* Floating Icon */}
        <motion.div
          animate={{
            y: [0, -4, 0],
            rotate: [0, 3, -3, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
          }}
          whileHover={{
            scale: 1.15,
            rotate: 8,
          }}
          className={`
            relative
            flex
            h-11
            w-11
            sm:h-12
            sm:w-12
            md:h-14
            md:w-14
            items-center
            justify-center
            rounded-xl
            md:rounded-2xl
            bg-gradient-to-br
            ${service.gradient}
            shadow-[0_10px_25px_rgba(0,0,0,.15)]
            md:shadow-[0_15px_35px_rgba(0,0,0,.18)]
          `}
        >
          <Icon className="text-base sm:text-lg md:text-xl text-white" />
        </motion.div>

        {/* Title */}
        <h3 className="mt-3 md:mt-4 text-lg sm:text-xl md:text-[22px] lg:text-2xl font-semibold leading-tight text-slate-900">
          {service.title}
        </h3>

        {/* Description */}
        <p className="mt-2 min-h-[56px] sm:min-h-[64px] md:min-h-[72px] text-xs sm:text-[13px] md:text-[14px] leading-5 md:leading-6 text-slate-600 transition-all duration-500 md:group-hover:-translate-y-2">
          {service.description}
        </p>

        {/* Divider */}
        <div className="my-3 md:my-4 h-px bg-slate-100/80"></div>
                {/* Features */}
                <div className="flex-1 space-y-2 mb-3 sm:mb-4 transition-all duration-500 md:group-hover:-translate-y-2">
          {service.features.slice(0, 3).map((feature, i) => (
            <motion.div
              key={i}
              className="flex items-center gap-2"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.2 }}
            >
              <div
                className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-gradient-to-r ${service.gradient}`}
              />

              <span className="text-xs sm:text-[13px] md:text-[14px] text-slate-700 leading-5">
                {feature}
              </span>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        {showLearnMore && (
        <motion.div
          whileHover={{ x: 4 }}
          className="mt-auto flex items-center justify-between"
        >
          <span className="text-[12px] sm:text-[13px] md:text-sm font-semibold text-slate-700 transition-colors duration-300 group-hover:text-slate-900">
            Learn More
          </span>

          <div
            className={`
              flex
              h-8
              w-8
              sm:h-9
              sm:w-9
              md:h-10
              md:w-10
              items-center
              justify-center
              rounded-full
              bg-gradient-to-r
              ${service.gradient}
              text-white
              shadow-lg
              transition-all
              duration-300
              group-hover:scale-110
              group-hover:rotate-12
            `}
          >
            <FaArrowRight className="text-[11px] sm:text-xs md:text-sm" />
          </div>
        </motion.div>
          )}
        {/* Premium Bottom Line */}
        <div
          className={`
            absolute
            bottom-0
            left-0
            h-[4px]
            w-full
            md:w-[70px]
            rounded-r-full
            bg-gradient-to-r
            ${service.gradient}
            transition-all
            duration-700
            ease-[cubic-bezier(.22,1,.36,1)]
            md:group-hover:w-full
            shadow-[0_0_18px_rgba(236,72,153,.35)]
          `}
        />
      </div>
    </motion.div>
  );
};

export default ServiceCard;